import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import ar from "./locales/ar.json";
import en from "./locales/en.json";
import fr from "./locales/fr.json";
import ur from "./locales/ur.json";

export const RTL_LANGUAGES = ["ar", "ur"];

// Urdu is enabled in production as the 4th supported language.
// Full human/AI translation shipped 2026-07-21 (2237/2237 keys).
// Env override still available: set VITE_ENABLE_URDU=false to hide.
const URDU_ENABLED = import.meta.env.VITE_ENABLE_URDU !== "false";

const ALL_LANGUAGES = [
  { code: "ar", label: "العربية", dir: "rtl" as const },
  { code: "en", label: "English", dir: "ltr" as const },
  { code: "fr", label: "Français", dir: "ltr" as const },
  { code: "ur", label: "اردو", dir: "rtl" as const },
];

export const SUPPORTED_LANGUAGES = URDU_ENABLED
  ? ALL_LANGUAGES
  : ALL_LANGUAGES.filter((l) => l.code !== "ur");

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ar: { translation: ar },
      en: { translation: en },
      fr: { translation: fr },
      ur: { translation: ur },
    },
    fallbackLng: "ar",
    supportedLngs: URDU_ENABLED ? ["ar", "en", "fr", "ur"] : ["ar", "en", "fr"],
    interpolation: { escapeValue: false },
    // Crash-proof translation: never return null/undefined, never throw on missing keys.
    returnNull: false,
    returnEmptyString: false,
    saveMissing: false,
    // If a key is missing in every fallback chain, return the human-friendly
    // last segment of the key instead of crashing the render tree.
    parseMissingKeyHandler: (key, defaultValue) => {
      if (defaultValue && typeof defaultValue === "string") return defaultValue;
      if (!key) return "";
      const last = String(key).split(".").pop() || String(key);
      // Convert "someKey" / "some_key" → "Some Key" for readability.
      return last
        .replace(/[_-]+/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
    },
  });

export const applyDirection = (lang: string) => {
  const dir = RTL_LANGUAGES.includes(lang) ? "rtl" : "ltr";
  document.documentElement.setAttribute("dir", dir);
  document.documentElement.setAttribute("lang", lang);
};

i18n.on("languageChanged", applyDirection);
applyDirection(i18n.language || "ar");

export default i18n;
